/**
 * Google Ads conversion tracking.
 *
 * Scope note: this exists to answer one question — which ad clicks became
 * sign-ups — and nothing else. PostHog remains the product analytics; Google
 * gets the single conversion signal its bidding needs and no further browsing
 * data, which is why the tag is mounted only on the public marketing surface
 * (see components/GoogleTags.tsx) and never inside the authenticated app.
 *
 * Everything here is inert until NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID is set,
 * so an unconfigured environment — local dev included — loads no Google script
 * and fires no conversions.
 */

/** e.g. "AW-17123456789". Public by design: it ships in the client bundle. */
export const GOOGLE_ADS_ID = process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID ?? ''

/** The per-action label, e.g. "AbC-D_efGhIjKlMnOp". */
export const SIGNUP_CONVERSION_LABEL =
  process.env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_LABEL ?? ''

export const googleAdsEnabled = () => GOOGLE_ADS_ID.length > 0

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

/**
 * Record a sign-up request as a Google Ads conversion.
 *
 * Called from the sign-up form, in the SAME browser session as the ad click —
 * the visitor goes ad → landing → pricing → this form without leaving. That
 * matters: gtag reads the click id from the `_gcl_aw` cookie it set on the
 * landing page, so no gclid plumbing of our own is needed. (Completing the
 * account is a different story — that happens after a magic-link round trip,
 * possibly on another device, and needs an offline import instead.)
 *
 * `user_data.email` powers enhanced conversions for leads: gtag hashes it
 * locally before it leaves the browser, and Google matches it against the
 * click. It is what keeps attribution working when the cookie is missing.
 */
export function trackSignupConversion(email: string) {
  if (!googleAdsEnabled() || !SIGNUP_CONVERSION_LABEL) return
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return

  try {
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${SIGNUP_CONVERSION_LABEL}`,
      user_data: { email },
    })
  } catch {
    // Analytics must never take down a sign-up. Same contract as
    // lib/posthog-server.ts's no-op client.
  }
}
